import { useEffect, useState } from 'react';
import { getLeaderboard, type LeaderboardRow } from '../api';
import type { RoundResult } from '../wire';

export interface GameOverProps {
  result: RoundResult;
  onPlayAgain: () => void; // rematch -> back to lobby with same team
  onLeave: () => void; // exit -> landing
  canRematch: boolean; // false when connection is closed/reconnecting (rematch msg would be dropped)
}

/** PER-SCREEN 11 fix: standard competition ranking ("1224") over a
 * PRE-SORTED-DESCENDING `team_total` column -- equal totals share the same
 * rank (rendered "T-N", tie-broken only by row order for the badge's own
 * `#`), and the rank AFTER a tie skips the count of tied rows, matching how
 * every real leaderboard/scoreboard handles a dead heat. Pure derivation off
 * the already-sorted `leaderboard` array (no new server call, no re-sort --
 * SPEC.md §1.1 stays intact), so a leaderboard that ISN'T actually sorted
 * descending would just produce a locally-nonsensical (but never crashing)
 * rank sequence, same as today's plain `i + 1` did. */
function rankOf(rows: LeaderboardRow[], i: number): { rank: number; tied: boolean } {
  const total = rows[i]!.team_total;
  const tiedWithPrev = i > 0 && rows[i - 1]!.team_total === total;
  const tiedWithNext = i < rows.length - 1 && rows[i + 1]!.team_total === total;
  if (!tiedWithPrev && !tiedWithNext) return { rank: i + 1, tied: false };
  // Walk back to the first row sharing this total -- that row's 1-based
  // index IS this tie group's shared rank (1224 ranking).
  let start = i;
  while (start > 0 && rows[start - 1]!.team_total === total) start -= 1;
  return { rank: start + 1, tied: true };
}

export function GameOver({ result, onPlayAgain, onLeave, canRematch }: GameOverProps): JSX.Element {
  const sorted = [...result.players].sort((a, b) => b.score - a.score);
  const durationS = Math.round(result.durationMs / 1000);

  const [leaderboard, setLeaderboard] = useState<LeaderboardRow[] | null>(null);
  const [leaderboardError, setLeaderboardError] = useState(false);
  // PART 5a: optimistic pending feedback -- the server transition is fast,
  // but with no local acknowledgement at all the click could look inert.
  // Local UI state ONLY (never a fake/derived server transition -- this
  // screen simply unmounts once the authoritative `lobby` broadcast
  // arrives, per App.tsx's own routing). If the server never responds the
  // button just stays pending -- that's the honest outcome, not covered up.
  const [rematchPending, setRematchPending] = useState(false);

  function handlePlayAgain(): void {
    setRematchPending(true);
    onPlayAgain();
  }

  useEffect(() => {
    let cancelled = false;
    getLeaderboard(10)
      .then((res) => {
        if (!cancelled) setLeaderboard(res.rows);
      })
      .catch(() => {
        if (!cancelled) setLeaderboardError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="screen gameover-screen" data-testid="screen-gameover">
      <h1>Game Over</h1>
      <p className="dim">
        Level {Math.max(...result.players.map((p) => p.level))} · {durationS}s
      </p>

      {/* PER-SCREEN 11 fix: the per-seat "this round" table and the
          all-time "Leaderboard" below it previously carried near-identical
          styling with no label of its own on the first table, so the two
          tables' hierarchy (THIS round vs ALL-TIME) wasn't obvious at a
          glance. A parallel "This Round" heading (2 words -- identity-length,
          same pixel-font policy as the existing "Leaderboard" h2 right below
          it) gives both tables a matching label; `.leaderboard-table`'s own
          distinct panel tint (index.css) carries the rest of the visual
          differentiation. */}
      <h2>This Round</h2>
      <table className="results-table" aria-label="Final scores">
        <thead>
          <tr>
            <th>#</th>
            <th>Seat</th>
            <th>Name</th>
            <th className="num">Score</th>
            <th className="num">Level</th>
            <th className="num">Lives left</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((p, i) => (
            <tr key={p.seat}>
              <td>
                <span className={`rank-badge${i < 3 ? ` rank-${i + 1}` : ''}`}>{i + 1}</span>
              </td>
              <td>P{p.seat}</td>
              <td>{p.name}</td>
              <td className="num">
                <span className="score-value">{p.score.toLocaleString()}</span>
              </td>
              <td className="num">{p.level}</td>
              <td className="num">{p.lives}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2>Leaderboard</h2>
      {leaderboardError && <p className="dim">Leaderboard unavailable right now.</p>}
      {!leaderboardError && !leaderboard && <p className="dim">Loading leaderboard...</p>}
      {leaderboard && leaderboard.length === 0 && <p className="dim">No completed rounds yet.</p>}
      {leaderboard && leaderboard.length > 0 && (
        <table className="results-table leaderboard-table" aria-label="Leaderboard, top team totals" data-testid="leaderboard">
          <thead>
            <tr>
              <th>#</th>
              <th>Team</th>
              <th className="num">Team total</th>
            </tr>
          </thead>
          <tbody>
            {leaderboard.map((row, i) => {
              const isYourRow = row.room_code === result.code;
              const { rank, tied } = rankOf(leaderboard, i);
              return (
                <tr key={row.room_code} className={isYourRow ? 'row-highlight' : undefined}>
                  <td>
                    <span className={`rank-badge${rank <= 3 ? ` rank-${rank}` : ''}`}>{tied ? `T-${rank}` : rank}</span>
                  </td>
                  {/* PART 5b: team IDENTITY, not a bare room code. team_name is
                      now composed server-side from THAT round's own
                      game_results seat rows ("P1name & P2name", or just the
                      solo name for a one-seat round) -- see
                      server/db/d1/0002_leaderboard_team_from_results.sql /
                      composeTeamName() in server/src/store/types.ts -- so it
                      no longer depends on the room row surviving. The
                      `?? row.room_code` here is now only a defensive
                      fallback for the degenerate case of a round with no
                      player rows at all; it should essentially never fire.
                      row-highlight above still matches on room_code (the
                      real join key), unaffected by which label is shown.
                      PER-SCREEN 11 fix: a small "YOU" chip identifies your OWN
                      row without the whole-row accent stripe out-weighing rank
                      #1's own gold emphasis (the previous defect: a #2 "you"
                      row visually read as the winner over the actual #1 team --
                      see `.row-highlight` / `.rank-badge.rank-1` in index.css). */}
                  <td>
                    {row.team_name ?? row.room_code}
                    {isYourRow && (
                      <span className="you-chip" data-testid="you-chip">
                        YOU
                      </span>
                    )}
                  </td>
                  <td className="num">
                    <span className="score-value">{row.team_total.toLocaleString()}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {!canRematch && <p className="dim">Reconnecting... you can play again once you're back online.</p>}
      <div className="gameover-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={handlePlayAgain}
          disabled={!canRematch || rematchPending}
          autoFocus
          data-testid="btn-play-again"
        >
          {rematchPending ? 'Returning to lobby...' : 'Play again with the same team'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onLeave} data-testid="btn-leave">
          Leave
        </button>
      </div>
    </div>
  );
}
